import { useContext, useEffect, useMemo, useState } from 'react'
import { useParams, Navigate, useNavigate } from "react-router-dom"
import { Box } from '@mui/material'

import { AppContext } from '/providers/app-context'

import PageWrapper from '/components/page-wrapper'
import HelpText from '/help/template-issued-passes'
import ManagePassesMenu from '/components/passes/manage-passes-menu'
import TemplateSelector from '/components/templates/template-selector'
import TemplatePassesTable from '/components/passes/template-passes-table'
import { getCurrentTemplateId } from '/utils/current-template'

const IssuedPasses = () => {

  const navigate = useNavigate()
  const { templateId } = useParams()
  const { loadingTemplates, templates } = useContext(AppContext)
  const [selectedPass, setSelectedPass] = useState(null)
  const [selectedPasses, setSelectedPasses] = useState({ type: 'loaded', count: 0, mode: 'live', passes: [] })
  const [deletedPassIds, setDeletedPassIds] = useState([])

  const template = useMemo(() => {return templates.filter(t => t.template_id === templateId)[0]}, [templates, templateId])

  const heading = useMemo(() => template ? `${template.name} ${template.archived ? " [archived]" : ""}` : "",[template])

  // The selector can display the remembered template while this route has no
  // template parameter.  Move to the concrete route so the table is mounted
  // and loads its first page without requiring another selector change.
  useEffect(() => {
    if (templateId || loadingTemplates) return

    const currentTemplateId = getCurrentTemplateId()
    const currentTemplate = templates.find(item =>
      String(item.template_id) === String(currentTemplateId) && !item.archived
    )

    if (currentTemplate) navigate(`/templates/${currentTemplate.template_id}/passes`, { replace: true })
  }, [loadingTemplates, navigate, templateId, templates])

  if (!loadingTemplates && !template && templateId) return <Navigate to="/templates" replace />

  const selectTemplate = selectedTemplateId => {
    setSelectedPass(null)
    setSelectedPasses({ type: 'loaded', count: 0, mode: 'live', passes: [] })
    navigate(selectedTemplateId ? `/templates/${selectedTemplateId}/passes` : '/manage-passes')
  }

  const handleSelectedPassesDeleted = passIds => {
    setSelectedPass(null)
    setSelectedPasses({ type: 'loaded', count: 0, mode: 'live', passes: [] })
    setDeletedPassIds(passIds)
  }

  if (!templateId) return (
    <PageWrapper title="Manage Passes" HelpText={HelpText} heading="Manage Passes">
      <Box sx={{ p: 3 }}>
        <TemplateSelector templateId={templateId} onChange={selectTemplate} />
      </Box>
    </PageWrapper>
  )

  return (
    <PageWrapper title={template ? `Issued Passes (${template.name})` : 'Issued Passes'} HelpText={HelpText} heading={heading} selectedTemplate={template} menu={<ManagePassesMenu template={template} selectedPass={selectedPass} selectedPasses={selectedPasses} onSelectedPassesDeleted={handleSelectedPassesDeleted} />}>
      <TemplatePassesTable
        templateId={templateId}
        deletedPassIds={deletedPassIds}
        onSelectTemplate={selectTemplate}
        onSelectedPassChange={setSelectedPass}
        onSelectedPassesChange={setSelectedPasses}
        onRowDoubleClick={pass => navigate(`/templates/${templateId}/passes/${pass.pass_id}/details`, { state: { pass } })}
      />
    </PageWrapper>
  )
}

export default IssuedPasses
